import {
  ArrowForwardRounded,
  ArrowRightAltRounded,
  CheckCircleRounded,
  CloseRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTransferable, useTransferCredits } from "@/hooks/useCredits";
import type { CreditTransferResult, SendableLot } from "@/interfaces/credits";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { daysUntil } from "@/utils/date-format";

const fmt = (n: number | string) => Number(n).toLocaleString("en-US");
// UTC so the calendar date matches the stored value regardless of the viewer's timezone. The lot
// table in the wallet does the same; the two must not disagree by a day about the same lot.
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

/** Credits are exact to the cent; a float total picks up a tail that makes a cap comparison lie. */
const round2 = (n: number) => Math.round(n * 100) / 100;
const EPSILON = 0.0001;

const CARD = {
  border: "1px solid var(--color-smoke)",
  borderRadius: "12px",
  bgcolor: "var(--color-white)",
  p: "18px 20px",
};

const CARD_TITLE = {
  fontFamily: "var(--font-crimson)",
  fontSize: 18,
  color: "var(--color-rodeo-black)",
};

const TH = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  // Text-safe dust (4.83:1 on white). --color-dark-dust is 2.8:1 as text and is for
  // icons/borders only — index.css says so at the token itself.
  color: "var(--color-dust-ink)",
};

/**
 * Send credits to another group in the same organisation.
 *
 * ALLOCATED PER LOT, not as one amount, and that is the whole design rather than a UI flourish:
 * each lot the client gives up arrives in the other group carrying its ORIGINAL expiry date. An
 * amount alone could not say which maturities were handed over, and merging them would quietly
 * refresh the clock on credits that were about to lapse. It is also why the confirmation lists the
 * destination lots and their dates — the promise the screen makes is the one thing worth proving.
 *
 * The cap is what the group may SEND, which is less than its balance whenever a live campaign is
 * holding headroom. Enforced on the server too — this is the explanation, not the guarantee.
 */
export default function TransferCreditsModal({
  open,
  groupId,
  groupName,
  onClose,
}: {
  open: boolean;
  groupId: number;
  groupName: string;
  onClose: () => void;
}) {
  const { data: transferable, isLoading, error: loadError } = useTransferable(groupId, open);
  const transfer = useTransferCredits(groupId);

  const [destination, setDestination] = useState<number | "">("");
  /** Keyed by lot id; the raw string the user typed, so a half-typed "1." doesn't jump around. */
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [note, setNote] = useState("");
  /** The one-shot "send this much, oldest first" box. Not part of the submitted body. */
  const [quickFill, setQuickFill] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** Set on success: the dialog stays open and becomes the receipt. */
  const [done, setDone] = useState<CreditTransferResult | null>(null);

  // The server decides what is sendable — live, confirmed, unexpired, something left in it — and
  // returns it with the cap. Filtering a lot list here would be a second definition of "sendable"
  // that could disagree with the one the transfer actually enforces. Sorted defensively: the fill
  // below is FIFO by maturity, so it must not depend on the order the rows happened to arrive in.
  const sendable = useMemo(
    () =>
      [...(transferable?.lots ?? [])].sort((a, b) =>
        a.expirationDate.localeCompare(b.expirationDate),
      ),
    [transferable],
  );

  const allocated = useMemo(
    () => round2(Object.values(amounts).reduce((sum, v) => sum + (Number(v) || 0), 0)),
    [amounts],
  );
  const cap = Number(transferable?.transferable ?? 0);
  const overCap = allocated > cap + EPSILON;
  const overLot = sendable.some((l) => (Number(amounts[l.lotId]) || 0) > Number(l.free) + EPSILON);
  const capLeft = Math.max(0, round2(cap - allocated));
  const targets = transferable?.targets ?? [];
  const destinationName = targets.find((t) => t.id === destination)?.name ?? "";

  /** What this lot may still take: its own balance, less whatever the OTHER lots already claim. */
  const maxForLot = (lot: SendableLot) => {
    const others = round2(allocated - (Number(amounts[lot.lotId]) || 0));
    return Math.max(0, round2(Math.min(Number(lot.free), cap - others)));
  };

  /**
   * Fill soonest-to-expire first.
   *
   * The same order every other draw in the system uses, and the order that serves the client: the
   * credits closest to lapsing are the ones worth moving to a group that will actually spend them.
   * Clamped to the cap, so the shortcut can never build a state the Transfer button would refuse.
   */
  const applyQuickFill = () => {
    let left = Math.min(Number(quickFill) || 0, cap);
    if (left <= 0) return;
    const next: Record<number, string> = {};
    for (const lot of sendable) {
      if (left <= EPSILON) break;
      const take = round2(Math.min(left, Number(lot.free)));
      if (take > 0) next[lot.lotId] = String(take);
      left = round2(left - take);
    }
    setAmounts(next);
    setQuickFill("");
  };

  const close = () => {
    // Never abandon the dialog with the request in flight — the transfer would still land, and the
    // person who dismissed it would have no idea whether it had.
    if (transfer.isPending) return;
    setDestination("");
    setAmounts({});
    setNote("");
    setQuickFill("");
    setError(null);
    setDone(null);
    transfer.reset();
    onClose();
  };

  const submit = async () => {
    setError(null);
    const allocations = Object.entries(amounts)
      .map(([lotId, credits]) => ({ lotId: Number(lotId), credits: Number(credits) }))
      .filter((a) => a.credits > 0)
      .map((a) => ({ lotId: a.lotId, credits: a.credits.toFixed(2) }));
    if (allocations.length === 0 || destination === "") return;

    try {
      const result = await transfer.mutateAsync({
        destinationGroupId: destination as number,
        allocations,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setDone(result);
    } catch (err) {
      // The server's own words: "4,200 of your 5,000 is budgeted to campaigns" is actionable in a
      // way that a generic failure is not.
      setError(apiErrorMessage(err, "The transfer could not be completed."));
    }
  };

  /** Why the button is off. A dead control that says nothing is a support ticket. */
  const blocker =
    destination === ""
      ? "Choose a group to transfer to."
      : allocated <= 0
        ? "Allocate credits from at least one lot."
        : overLot
          ? "One lot has more allocated than it holds."
          : null;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="md"
      fullWidth
      aria-labelledby="transfer-credits-title"
    >
      <DialogTitle
        id="transfer-credits-title"
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1.5,
          pr: 6,
          ...CARD_TITLE,
          fontSize: 22,
        }}
      >
        {done ? "Credits transferred" : "Transfer credits"}
        <Typography
          component="span"
          sx={{ color: "var(--color-graphite)", fontSize: 14, fontFamily: "var(--font-lato)" }}
        >
          from {groupName}
        </Typography>
        <IconButton
          onClick={close}
          aria-label="Close"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "var(--color-background)" }}>
        {done ? (
          <TransferReceipt result={done} destinationName={destinationName} />
        ) : isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress aria-label="Loading" size={28} />
          </Box>
        ) : loadError ? (
          // Distinct from the empty state below on purpose: a failed request that renders as "you
          // have no sibling groups" sends someone to check their org structure over a 404.
          <Alert severity="error" role="alert">
            {apiErrorMessage(loadError, "Could not load this group's transferable balance.")}
          </Alert>
        ) : targets.length === 0 ? (
          <Alert severity="info">
            This group has no sibling groups to transfer to. Credits can only move between groups of
            the same organisation.
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* ── Route: both ends of the move, and what may leave ─────────────────────── */}
            <Box sx={CARD}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography sx={TH}>From</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 0.4 }}>
                    {groupName}
                  </Typography>
                </Box>
                <ArrowRightAltRounded sx={{ color: "var(--color-dark-dust)", fontSize: 30 }} />
                <TextField
                  select
                  size="small"
                  autoFocus
                  label="Transfer to"
                  value={destination}
                  onChange={(e) => setDestination(Number(e.target.value))}
                  sx={{ minWidth: 240 }}
                >
                  {targets.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Box sx={{ ml: "auto", textAlign: "right" }}>
                  <Typography sx={TH}>Available to transfer</Typography>
                  <Typography
                    sx={{ fontFamily: "var(--font-crimson)", fontSize: 24, lineHeight: 1.2 }}
                  >
                    {fmt(cap)}
                  </Typography>
                  {Number(transferable?.budgetedToCampaigns ?? 0) > 0 && (
                    <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                      {fmt(transferable?.balance ?? 0)} held, less{" "}
                      {fmt(transferable?.budgetedToCampaigns ?? 0)} budgeted to campaigns
                    </Typography>
                  )}
                </Box>
              </Box>

              <Alert severity="info" icon={false} sx={{ mt: 1.5, fontSize: 13 }}>
                You can send your credits to a teammate's account. Sending them doesn't change their
                expiration date, if your credits expire October 1, they'll still expire October 1
                after the transfer.
              </Alert>
            </Box>

            {/* ── Allocate: the grain is the maturity ──────────────────────────────────── */}
            {sendable.length === 0 ? (
              <Alert severity="warning">This group has no credits available to transfer.</Alert>
            ) : cap <= 0 ? (
              // Lots exist but none of them may leave. Saying "no credits" here would be a lie the
              // wallet's own balance contradicts.
              <Alert severity="warning">
                All {fmt(transferable?.balance ?? 0)} of this group's credits are budgeted to live
                campaigns, so none can be transferred. Closing or archiving a campaign releases what
                it has not spent.
              </Alert>
            ) : (
              <Box sx={CARD}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <Typography sx={CARD_TITLE}>Allocate</Typography>
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Send this much"
                      value={quickFill}
                      onChange={(e) => setQuickFill(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyQuickFill();
                        }
                      }}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: cap,
                          inputMode: "decimal",
                          "aria-label": "Amount to spread across lots, soonest to expire first",
                        },
                      }}
                      sx={{ width: 150, ...NO_SPINNER }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={applyQuickFill}
                      disabled={!(Number(quickFill) > 0)}
                      sx={{ height: 40, whiteSpace: "nowrap" }}
                    >
                      Fill oldest first
                    </Button>
                  </Box>
                </Box>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...TH, width: 48 }}>#</TableCell>
                      <TableCell sx={TH}>Expires on</TableCell>
                      <TableCell align="right" sx={TH}>
                        Spare on this lot
                      </TableCell>
                      <TableCell align="right" sx={{ ...TH, width: 210 }}>
                        Allocate
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sendable.map((lot, i) => {
                      const typed = Number(amounts[lot.lotId]) || 0;
                      const tooMuch = typed > Number(lot.free) + EPSILON;
                      const days = daysUntil(lot.expirationDate);
                      return (
                        <TableRow key={lot.lotId}>
                          <TableCell sx={{ color: "var(--color-graphite)" }}>{i + 1}</TableCell>
                          <TableCell>
                            {fmtDate(lot.expirationDate)}
                            <Typography
                              sx={{
                                fontSize: 12,
                                color:
                                  days <= 30 ? "var(--color-good-wine)" : "var(--color-graphite)",
                              }}
                            >
                              {days > 0 ? `in ${days} day${days === 1 ? "" : "s"}` : "expired"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {fmt(lot.free)}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 0.5,
                              }}
                            >
                              <TextField
                                size="small"
                                type="number"
                                value={amounts[lot.lotId] ?? ""}
                                error={tooMuch}
                                helperText={tooMuch ? "More than this lot has spare" : undefined}
                                onChange={(e) =>
                                  setAmounts((prev) => ({ ...prev, [lot.lotId]: e.target.value }))
                                }
                                // A scroll over a focused number input silently changes the value;
                                // in a money field that is a wrong transfer nobody typed.
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                slotProps={{
                                  htmlInput: {
                                    min: 0,
                                    max: Number(lot.free),
                                    inputMode: "decimal",
                                    "aria-label": `Credits to transfer from the lot expiring ${fmtDate(lot.expirationDate)}`,
                                  },
                                }}
                                sx={{ width: 130, ...NO_SPINNER }}
                              />
                              <Button
                                size="small"
                                onClick={() =>
                                  setAmounts((prev) => ({
                                    ...prev,
                                    [lot.lotId]: String(maxForLot(lot)),
                                  }))
                                }
                                sx={{
                                  minWidth: 0,
                                  px: 0.75,
                                  color: "var(--color-dust-ink)",
                                  alignSelf: tooMuch ? "flex-start" : "center",
                                  mt: tooMuch ? 1 : 0,
                                }}
                                aria-label={`Allocate the maximum from the lot expiring ${fmtDate(lot.expirationDate)}`}
                              >
                                Max
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: "var(--color-rodeo-black)" }}>
                          Total allocated
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, pr: "62px" }}>
                        <Typography
                          aria-live="polite"
                          sx={{
                            fontWeight: 700,
                            fontSize: 16,
                            fontVariantNumeric: "tabular-nums",
                            color: overCap ? "var(--color-good-wine)" : "var(--color-rodeo-black)",
                          }}
                        >
                          {fmt(allocated)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                          {fmt(capLeft)} still available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </Box>
            )}

            {/* ── Note: already carried by the transfer row AND the journal memo ───────── */}
            <Box sx={CARD}>
              <Typography sx={{ ...CARD_TITLE, mb: 1.25 }}>Note</Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Why are these credits moving? (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                helperText={`${note.length}/500 — shown in the group's transfer history and carried onto the accounting entry as its memo.`}
              />
            </Box>

            {overCap && (
              <Alert severity="error" role="alert">
                {fmt(allocated)} allocated, but only {fmt(cap)} can be transferred — the rest is
                budgeted to live campaigns.
              </Alert>
            )}
            {error && (
              <Alert severity="error" role="alert">
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {done ? (
          <Button variant="contained" onClick={close} sx={{ bgcolor: "var(--color-dark-dust)" }}>
            Done
          </Button>
        ) : (
          <>
            {blocker && targets.length > 0 && sendable.length > 0 && cap > 0 && (
              <Typography
                aria-live="polite"
                sx={{ mr: "auto", fontSize: 12.5, color: "var(--color-graphite)" }}
              >
                {blocker}
              </Typography>
            )}
            <Button onClick={close} disabled={transfer.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                transfer.isPending ? (
                  <CircularProgress aria-label="Loading" size={16} sx={{ color: "white" }} />
                ) : (
                  <ArrowForwardRounded />
                )
              }
              onClick={submit}
              disabled={
                transfer.isPending ||
                destination === "" ||
                allocated <= 0 ||
                overCap ||
                overLot ||
                targets.length === 0
              }
              sx={{ bgcolor: "var(--color-dark-dust)" }}
            >
              {transfer.isPending
                ? "Transferring…"
                : allocated > 0
                  ? `Transfer ${fmt(allocated)}`
                  : "Transfer"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Number inputs: the spinners are noise in a money field and the arrows invite mis-clicks. */
const NO_SPINNER = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

/**
 * The receipt — one row per destination lot.
 *
 * The screen's whole promise is that a transferred credit keeps the expiry it was bought with, and
 * this is the only place that promise can be checked. Closing straight back to the wallet would
 * leave a client to go and look for it.
 */
function TransferReceipt({
  result,
  destinationName,
}: {
  result: CreditTransferResult;
  destinationName: string;
}) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
        <CheckCircleRounded sx={{ color: "var(--color-dark-dust)" }} />
        <Typography sx={{ ...CARD_TITLE, fontSize: 20 }}>
          {fmt(result.credits)} credits moved{destinationName ? ` to ${destinationName}` : ""}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13.5, color: "var(--color-graphite)", mb: 2 }}>
        They arrived as {result.lines.length} lot{result.lines.length === 1 ? "" : "s"}, each
        keeping the expiry date it had here.
      </Typography>

      <Box sx={CARD}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={TH}>Credits</TableCell>
              <TableCell sx={TH}>Expires on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.lines.map((line) => (
              <TableRow key={line.lineId}>
                <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {fmt(line.credits)}
                </TableCell>
                <TableCell>{fmtDate(line.expirationDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
